import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import UserSummaryCard from "../../components/Profile/UserSummaryCard";
import WalletSummaryCard from "../../components/Profile/WalletSummaryCard";
import WalletStatsCard from "../../components/Profile/WalletStatsCard";
import DataTable from "../../components/Elements/DataTable/DataTable";
import EditUserModal from "../../components/Profile/EditUserModal";

import WalletService from "../../services/WalletService";
import { fetchWalletTransactions } from "../../services/TransactionsFetchers";
import { fetchInvoicePdf } from "../../services/TransactionService";
import { downloadBlob, parseFilename } from "../../utils/downloadFile";
import MyAccountService from "../../services/MyAccountService";
import { AuthContext } from "../../context/AuthContext";
import RefundModal from "../../components/Profile/RefundModal";

import "./Profile.css";

// Transaction table configuration
const txColumns = [
  { key: "id", label: "ID" },
  { key: "date", label: "Date" },
  { key: "amount", label: "Amount" },
  { key: "transaction_type", label: "Type" },
  { key: "success", label: "Status" },
];

const txOptionalColumns = [];

const txSortOptions = [
  { label: "Date", value: "date" },
  { label: "Amount", value: "amount" },
];

const txFilters = [
  {
    key: "transaction_type",
    type: "select",
    options: [
      { value: "service", label: "Service" },
      { value: "topup", label: "Top-Up" },
      { value: "transfer", label: "Transfer" },
    ],
  },
];

const Profile = () => {
  const navigate = useNavigate();
  const {
    token,
    userId,
    walletCurrency,
    loading: authLoading,
    setToken,
  } = useContext(AuthContext);
  console.log("User Profile: ", userId);

  // Core profile data
  const [userData, setUserData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [walletStats, setWalletStats] = useState(null);

  // Loading flags for separate sections
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);

  // UI state for modals
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false); // reserved for future transfer modal
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState("");

  // DODAJ console.log do getTableActions:
  const getTableActions = (item) => {
    // console.log("🔍 Checking item:", item.id, {
    //   payment_provider: item.payment_provider,
    //   payment_status: item.payment_status,
    // });

    const hasPaymentProvider =
      item.payment_provider &&
      item.payment_provider !== null &&
      item.payment_provider !== "";
    const hasSuccessPayment = item.payment_status === "succeeded";

    // console.log("✅ Conditions:", {
    //   hasPaymentProvider,
    //   hasSuccessPayment,
    //   showButtons: hasPaymentProvider && hasSuccessPayment,
    // });

    if (hasPaymentProvider && hasSuccessPayment) {
      const actions = [
        // {
        //   label: "Refund",
        //   onClick: (transaction) => {
        //     setSelectedTransactionId(transaction.id);
        //     setRefundModalOpen(true);
        //   },
        // },
        {
          label: "Download",
          onClick: async (transaction) => {
            try {
              const { blob, contentDisposition } = await fetchInvoicePdf(
                transaction.id,
              );
              const filename =
                parseFilename(contentDisposition) ||
                `invoice-${transaction.id}.pdf`;
              downloadBlob(blob, filename);
            } catch (e) {
              console.error("Invoice download failed:", e);
              setError("Failed to download invoice PDF.");
            }
          },
        },
      ];
      console.log("✅ Returning actions:", actions);
      return actions;
    }

    return [];
  };

  // Global error message for the page
  const [error, setError] = useState("");

  // Fetch profile data when auth state changes
  useEffect(() => {
    if (authLoading) return;

    // If user is not authenticated, stop loading and do not fetch
    if (!token || !userId || userId === -1) {
      setLoadingUser(false);
      setLoadingWallet(false);
      setLoadingStats(false);
      return;
    }

    const fetchAll = async () => {
      try {
        setError("");

        // 1. Load basic user profile
        setLoadingUser(true);
        const user = await MyAccountService.getUserData(userId, token);
        setUserData(user);
        setLoadingUser(false);

        // 2. Load main wallet
        setLoadingWallet(true);
        const wallet = await WalletService.getUserWallet(token);
        setWalletData(wallet);
        setLoadingWallet(false);

        // 3. Load wallet statistics if wallet exists
        if (wallet && wallet.id) {
          setLoadingStats(true);
          const stats = await WalletService.getWalletStats(wallet.id, token);
          setWalletStats(stats);
        }
      } catch (err) {
        console.error("Profile load error:", err);
        if (!error) {
          setError(err.message || "Could not load wallet statistics.");
        }
      } finally {
        setLoadingStats(false);
      }
    };

    fetchAll();
  }, [token, userId, authLoading]);

  const isLoading = loadingUser || loadingWallet || loadingStats;
  const hasBasics = userData && walletData;

  // Transactions table fetch function
  const txFetchFn = (params) =>
    fetchWalletTransactions({ ...params, walletId: walletData?.id });

  // Custom renderers for transaction table
  const txRenderers = {
    amount: (tx) => `${tx.amount.toFixed(2)} ${tx.currency}`,
    date: (item) => {
      if (!item.date) return "-";
      const date = new Date(item.date);
      return date.toLocaleString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    transaction_type: (item) => {
      const types = {
        topup: "Top-Up",
        service: "Service",
        transfer: "Transfer",
        api: "API",
        invoice: "Invoice",
      };
      return types[item.transaction_type] || item.transaction_type || "-";
    },
    success: (item) => (
      <span className={`status-badge ${item.success ? "success" : "failed"}`}>
        {item.success ? "Success" : "Failed"}
      </span>
    ),
  };

  // Delete account handler – logs user out and redirects on success
  const handleDeleteAccount = async () => {
    if (!userData || !token) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This action cannot be undone.",
    );
    if (!confirmed) return;

    try {
      await MyAccountService.deleteUser(userData.email, token);

      setToken(null);
      localStorage.removeItem("token");

      navigate("/sign-in");
    } catch (err) {
      setError(err.message || "Could not delete your account.");
    }
  };

  // Refresh wallet + stats after operations like transfer/topup
  const refreshProfileData = async () => {
    if (!token || !userId || !walletData?.id) return;

    try {
      const wallet = await WalletService.getUserWallet(token);
      setWalletData(wallet);

      if (wallet?.id) {
        const stats = await WalletService.getWalletStats(wallet.id, token);
        setWalletStats(stats);
      }

      console.log("Profile data refreshed after transfer");
    } catch (err) {
      console.error("Refresh failed:", err);
      setError("Failed to refresh data after transfer");
    }
  };

  return (
    <div className="profile-page">
      <PageHeader
        title="Profile Data & Wallet"
        description="View and manage your personal information and wallet balance."
      />

      {error && <p className="info-text error-text">{error}</p>}
      {isLoading && !userData && !walletData && (
        <p className="info-text">Loading data...</p>
      )}

      {hasBasics && (
        <>
          {/* Top row with user summary + wallet summary + stats */}
          <div className="profile-row">
            <UserSummaryCard
              user={{
                name: userData.username,
                email: userData.email,
                role: userData.role || "user",
              }}
              onEdit={() => setIsEditOpen(true)}
              onDelete={handleDeleteAccount}
            />

            <WalletSummaryCard
              wallet={walletData}
              userId={userId}
              token={token}
              onRefreshData={refreshProfileData}
            />

            <WalletStatsCard stats={walletStats} />
          </div>

          {/* User profile edit modal (controlled by Profile state) */}
          <EditUserModal
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            user={userData}
            token={token}
            onUpdated={(updated) => setUserData(updated)}
          />

          <RefundModal
            isOpen={refundModalOpen}
            onClose={() => setRefundModalOpen(false)}
            transactionId={selectedTransactionId}
            onSuccess={() => {
              setRefundModalOpen(false);
            }}
          />

          {/* Wallet transactions history */}
          <div className="profile-transactions">
            <DataTable
              fetchDataFunction={txFetchFn}
              columns={txColumns}
              optionalColumns={txOptionalColumns}
              sortOptions={txSortOptions}
              filterDefinitions={txFilters}
              defaultSortBy="date"
              itemKey="id"
              customRenderers={txRenderers}
              // rowActions={getTableActions}
              showCheckboxes={false}
              showFilters={false}
              showPageSize={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;
